import {
  createContext,
  Dispatch,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";
import { getDesignByUrl, getPageDesign } from "../services/api/apiClient";
import { Attribute } from "../services/api/types/portalConfiguration/portal";
import { usePortal } from "./PortalsContext";
import { useAuth } from "./AuthContext";
import {
  DesignTemplateGPC,
  DesignTemplateLogin,
  DesignTemplateHome,
  DesignTemplateCertificate,
  DesignTemplateProfile,
} from "../utils/seedDefaultCustomisation";
import { setFavicon } from "../utils/helpers";

type LoadStatus = "idle" | "loading" | "loaded" | "error";

export type PageUid = "GPC" | "LOGIN" | "HOME" | "CERTIFICATES" | "PROFILE";
export type PageSectionKey =
  | "logoSettings"
  | "backgroundSettings"
  | "textColorsSettings"
  | "navbarSettings"
  | "buttonsSettings"
  | "assignmentStatusColorsSettings"
  | "inputFieldsSettings"
  | "cardsSettings";

export type AttributeKey =
  // GPC logoSettings
  | "gpcFavicon"
  | "gpcLandscapeLogo"
  | "gpcSquareLogo"
  // GPC backgroundSettings
  | "gpcBackgroundImage"
  | "gpcBackgroundImageToggle"
  | "gpcBackgroundImageTransparency"
  | "gpcPrimaryBgColor"
  | "gpcSecondaryBgColor"
  | "gpcSidebarBgColor"
  // GPC textColorsSettings
  | "gpcPrimaryTextColor"
  | "gpcSecondaryTextColor"
  // GPC navbarSettings
  | "navbarBgColor"
  | "navbarDropdownBgColor"
  | "navbarDropdownTextColor"
  // GPC buttonsSettings
  | "gpcBtnPrimaryBgColor"
  | "gpcBtnPrimaryTextColor"
  | "gpcBtnSecondaryBgColor"
  | "gpcBtnSecondaryTextColor"
  | "gpcLinkColor"
  // GPC assignmentStatusColorsSettings
  | "gpcCompleted"
  | "gpcPending"
  | "gpcOverdue"
  | "gpcBorderColor"
  | "gpcBorderSize"
  | "gpcShadowColor"
  | "gpcShadowSize"
  // LOGIN logoSettings
  | "loginLandscapeLogo"
  | "loginPoweredBy"
  | "loginPoweredByLogoLight"
  | "loginPoweredByLogoDark"
  | "loginPoweredByLogoMode"
  // LOGIN backgroundSettings
  | "loginBackgroundImage"
  | "loginBackgroundImageToggle"
  | "loginBackgroundImageTransparency"
  | "loginPrimaryBgColor"
  | "loginSecondaryBgColor"
  // LOGIN textColorsSettings
  | "loginPrimaryTextColor"
  | "loginSecondaryTextColor"
  // LOGIN buttonsSettings
  | "loginBtnPrimaryBgColor"
  | "loginBtnPrimaryTextColor"
  | "loginBtnSecondaryBgColor"
  | "loginBtnSecondaryTextColor"
  | "loginLinkColor"
  // LOGIN inputFieldsSettings
  | "inputBorderColor"
  | "inputBGColor"
  // HOME backgroundSettings
  | "homeBackgroundImage"
  | "homeBackgroundImageToggle"
  | "homeBackgroundImageTransparency"
  | "homePrimaryBgColor"
  | "homeSecondaryBgColor"
  // HOME textColorsSettings
  | "homePrimaryTextColor"
  | "homeSecondaryTextColor"
  // HOME buttonsSettings
  | "homeBtnPrimaryBgColor"
  | "homeBtnPrimaryTextColor"
  | "homeBtnSecondaryBgColor"
  | "homeBtnSecondaryTextColor"
  | "homeLinkColor"
  // HOME assignmentStatusColorsSettings
  | "homeCompleted"
  | "homePending"
  | "homeOverdue"
  | "homeBorderColor"
  | "homeBorderSize"
  | "homeShadowColor"
  | "homeShadowSize"
  // HOME cardsSettings
  | "cardBorderColor"
  | "cardBorderSize"
  | "cardShadowColor"
  | "cardShadowSize"
  | "cardBackgroundColor"
  | "cardTransparencyLevel"
  | "cardNotStartedProgressBarColor"
  | "cardInProgressBarColor"
  | "cardCompletedProgressBarColor"
  | "cardCourseColor"
  | "cardSurveyColor"
  | "cardPolicyColor"
  | "cardOverdueTextColor"
  | "cardDateTextColor"
  // CERTIFICATE logoSettings
  | "certificatesLogo"
  // CERTIFICATE backgroundSettings
  | "certificatesBackgroundImage"
  | "certificatesBackgroundImageToggle"
  | "certificatesBackgroundImageTransparency"
  | "certificatesPrimaryBgColor"
  | "certificatesSecondaryBgColor"
  // CERTIFICATE textColorsSettings
  | "certificatesPrimaryTextColor"
  | "certificatesSecondaryTextColor"
  // CERTIFICATE buttonsSettings
  | "certificatesBtnPrimaryBgColor"
  | "certificatesBtnPrimaryTextColor"
  | "certificatesBtnSecondaryBgColor"
  | "certificatesBtnSecondaryTextColor"
  | "certificatesLinkColor"
  // PROFILE backgroundSettings
  | "profileBackgroundImage"
  | "profileBackgroundImageToggle"
  | "profileBackgroundImageTransparency"
  | "profilePrimaryBgColor"
  | "profileSecondaryBgColor"
  // PROFILE textColorsSettings
  | "profilePrimaryTextColor"
  | "profileSecondaryTextColor"
  // PROFILE buttonsSettings
  | "profileBtnPrimaryBgColor"
  | "profileBtnPrimaryTextColor"
  | "profileBtnSecondaryBgColor"
  | "profileBtnSecondaryTextColor"
  | "profileLinkColor";

export type CustomisationData = {
  [K in PageUid]: {
    [S in PageSectionKey]?: {
      [A in AttributeKey]?: string;
    };
  };
};

export type CustomisationState = {
  customisations: CustomisationData;
  loadStatus: LoadStatus;
};

const defaultCustomisationsArray = [
  ...DesignTemplateGPC,
  ...DesignTemplateLogin,
  ...DesignTemplateHome,
  ...DesignTemplateCertificate,
  ...DesignTemplateProfile,
];

const normalizeCustomisations = (
  arr: Array<{
    pageUid: PageUid;
    pageSectionKey: PageSectionKey;
    attributeKey: AttributeKey;
    attributeValue: string;
  }>,
): CustomisationData => {
  const acc: Partial<CustomisationData> = {};

  arr.forEach(({ pageUid, pageSectionKey, attributeKey, attributeValue }) => {
    if (!acc[pageUid]) acc[pageUid] = {};
    if (!acc[pageUid]![pageSectionKey]) acc[pageUid]![pageSectionKey] = {};
    acc[pageUid]![pageSectionKey]![attributeKey] = attributeValue;
  });

  return acc as CustomisationData;
};

const initialCustomisations = normalizeCustomisations(
  defaultCustomisationsArray,
);

type CustomisationAction =
  | {
      type: "UPDATE_PAGE_SECTION";
      payload: {
        pageUid: PageUid;
        pageSectionKey: PageSectionKey;
        updatedAttributes: Partial<Record<AttributeKey, string>>;
      };
    }
  | {
      type: "UPDATE_PAGE";
      payload: {
        pageUid: PageUid;
        updatedAttributes: Attribute[];
      };
    }
  | { type: "RESET_ALL_PAGES" }
  | {
      type: "SET_LOAD_STATUS";
      payload: LoadStatus;
    };

const CustomisationReducer = (
  state: CustomisationState,
  action: CustomisationAction,
): CustomisationState => {
  switch (action.type) {
    case "UPDATE_PAGE_SECTION": {
      const { pageUid, pageSectionKey, updatedAttributes } = action.payload;
      const page = state.customisations[pageUid] ?? {};
      const section = page[pageSectionKey] ?? {};

      return {
        ...state,
        customisations: {
          ...state.customisations,
          [pageUid]: {
            ...page,
            [pageSectionKey]: {
              ...section,
              ...updatedAttributes,
            },
          },
        },
      };
    }

    case "UPDATE_PAGE": {
      const { pageUid, updatedAttributes } = action.payload;
      // Build page from payload only so portal switch fully replaces data
      // (avoids keeping stale attributes like backgroundImageToggle from previous portal)
      const updatedPageSections: Partial<
        Record<PageSectionKey, Record<string, string>>
      > = {};

      updatedAttributes.forEach(
        ({ pageSectionKey, attributeKey, attributeValue }) => {
          if (pageSectionKey && attributeKey) {
            const section =
              updatedPageSections[pageSectionKey as PageSectionKey] ?? {};
            updatedPageSections[pageSectionKey as PageSectionKey] = {
              ...section,
              [attributeKey]: attributeValue,
            };
          }
        },
      );

      return {
        ...state,
        customisations: {
          ...state.customisations,
          [pageUid]: updatedPageSections as CustomisationData[PageUid],
        },
      };
    }

    case "RESET_ALL_PAGES": {
      return { ...state, customisations: initialCustomisations };
    }

    case "SET_LOAD_STATUS": {
      return {
        ...state,
        loadStatus: action.payload,
      };
    }

    default:
      return state;
  }
};

interface CustomisationContextType {
  customisations: CustomisationState;
  dispatch: Dispatch<CustomisationAction>;
  updateCustomisations: (pageUid: PageUid) => void;
  updateCustomisationSection: (
    pageUid: PageUid,
    pageSectionKey: PageSectionKey,
    updatedAttributes: Partial<Record<AttributeKey, string>>,
  ) => void;
  getCustomisationValue: (
    pageUid: PageUid,
    pageSectionKey: PageSectionKey,
    attributeKey: AttributeKey,
  ) => string;
}

const CustomisationContext = createContext<CustomisationContextType>({
  customisations: { customisations: initialCustomisations, loadStatus: "idle" },
  dispatch: () => {},
  updateCustomisations: () => {},
  updateCustomisationSection: () => {},
  getCustomisationValue: () => "",
});

export const CustomisationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [customisations, dispatch] = useReducer(CustomisationReducer, {
    customisations: initialCustomisations,
    loadStatus: "idle",
  });
  const { auth } = useAuth();
  const { portals } = usePortal();

  const getCustomisationValue = useCallback(
    (
      pageUid: PageUid,
      pageSectionKey: PageSectionKey,
      attributeKey: AttributeKey,
    ): string => {
      return (
        customisations.customisations[pageUid]?.[pageSectionKey]?.[
          attributeKey
        ] ?? ""
      );
    },
    [customisations.customisations],
  );

  const updateCustomisationSection = useCallback(
    (
      pageUid: PageUid,
      pageSectionKey: PageSectionKey,
      updatedAttributes: Partial<Record<AttributeKey, string>>,
    ) => {
      dispatch({
        type: "UPDATE_PAGE_SECTION",
        payload: { pageUid, pageSectionKey, updatedAttributes },
      });
    },
    [],
  );

  const updateCustomisations = useCallback(
    async (pageUid: PageUid) => {
      try {
        dispatch({
          type: "SET_LOAD_STATUS",
          payload: "loading",
        });

        let data: Attribute[] = [];

        if (auth.user) {
          const portalUid =
            portals.selectedPortal?.portalUid || auth.user.PortalUid;
          data = await getPageDesign(portalUid, pageUid);
        } else {
          data = await getDesignByUrl(pageUid, window.location.origin);
        }

        if (pageUid === "GPC" && data.length > 0) {
          const attribute = data.find(
            (attr) => attr.attributeKey === "gpcFavicon",
          );
          setFavicon(`/${attribute?.attributeValue}`);
        }

        dispatch({
          type: "UPDATE_PAGE",
          payload: { pageUid, updatedAttributes: data },
        });
      } catch (error) {
        console.log("Error:", error);
      } finally {
        dispatch({
          type: "SET_LOAD_STATUS",
          payload: "loaded",
        });
      }
    },
    [auth.user, portals.selectedPortal],
  );

  const memoizedValue = useMemo(
    () => ({
      customisations,
      dispatch,
      updateCustomisations,
      updateCustomisationSection,
      getCustomisationValue,
    }),
    [
      customisations,
      updateCustomisations,
      updateCustomisationSection,
      getCustomisationValue,
    ],
  );

  return (
    <CustomisationContext.Provider value={memoizedValue}>
      {children}
    </CustomisationContext.Provider>
  );
};

export const useCustomisation = () => useContext(CustomisationContext);
